// src/components/categories/CategoryForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createCategory,
  updateCategory,
  fetchCategory,
  fetchCategories,
  uploadCategoryImage,
  clearCategoryError,
  clearCategorySuccess,
} from '../../store/actions/categoryActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const CategoryForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedCategory, loading, error, success, categories } = useSelector(
    (state) => state.categories
  );

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    type: 'GENERAL',
    parentId: '',
    icon: '',
    color: '#2563eb',
    isActive: true,
    order: 0,
    metaTitle: '',
    metaDescription: '',
  });

  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const isEditMode = !!id;

  const typeOptions = [
    { value: 'GENERAL', label: 'Général' },
    { value: 'FAQ', label: 'FAQ' },
    { value: 'BLOG', label: 'Blog' },
    { value: 'PRODUCT', label: 'Produit' },
    { value: 'SUPPORT', label: 'Support' },
  ];

  const colorOptions = [
    '#2563eb', // blue
    '#dc2626', // red
    '#059669', // green
    '#d97706', // orange
    '#7c3aed', // purple
    '#db2777', // pink
    '#6b7280', // gray
  ];

  const iconOptions = [
    '📁', '📂', '📄', '📝', '📊', '📈', '📉',
    '💼', '🏢', '🏠', '🔧', '⚙️', '💡', '🎯',
    '⭐', '❤️', '✅', '📌', '📍', '🔖', '🏷️',
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    section: {
      marginBottom: '2rem',
      paddingBottom: '2rem',
      borderBottom: '1px solid #e5e7eb',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    fullWidth: {
      gridColumn: 'span 2',
    },
    fieldGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    select: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      backgroundColor: '#ffffff',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    slugPreview: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    imageUpload: {
      marginTop: '0.5rem',
    },
    imagePreview: {
      width: '100px',
      height: '100px',
      borderRadius: '0.5rem',
      objectFit: 'cover',
      backgroundColor: '#f3f4f6',
      border: '1px solid #e5e7eb',
      marginBottom: '1rem',
    },
    imageUploadButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    iconGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    iconButton: {
      padding: '0.5rem',
      fontSize: '1.25rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    iconButtonSelected: {
      borderColor: '#2563eb',
      backgroundColor: '#dbeafe',
    },
    colorGrid: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      marginTop: '0.5rem',
    },
    colorButton: {
      width: '40px',
      height: '40px',
      borderRadius: '0.375rem',
      border: '2px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    colorButtonSelected: {
      borderColor: '#111827',
      transform: 'scale(1.1)',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
  };

  useEffect(() => {
    dispatch(fetchCategories({ limit: 100, isActive: true }));
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchCategory(id));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (isEditMode && selectedCategory) {
      setFormData({
        name: selectedCategory.name || '',
        slug: selectedCategory.slug || '',
        description: selectedCategory.description || '',
        type: selectedCategory.type || 'GENERAL',
        parentId: selectedCategory.parentId || '',
        icon: selectedCategory.icon || '',
        color: selectedCategory.color || '#2563eb',
        isActive: selectedCategory.isActive ?? true,
        order: selectedCategory.order || 0,
        metaTitle: selectedCategory.metaTitle || '',
        metaDescription: selectedCategory.metaDescription || '',
      });
      if (selectedCategory.image) {
        setImagePreview(selectedCategory.image.storagePath);
      }
    }
  }, [selectedCategory, isEditMode]);

  // Generate slug from name
  useEffect(() => {
    if (!isEditMode && formData.name && !formData.slug) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.name, isEditMode]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearCategoryError());
      dispatch(clearCategorySuccess());
    };
  }, [dispatch]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Le slug est requis';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Le slug ne peut contenir que des lettres minuscules, des chiffres et des tirets';
    }

    if (!formData.type) {
      newErrors.type = 'Le type est requis';
    }

    if (formData.metaDescription && formData.metaDescription.length > 160) {
      newErrors.metaDescription = 'La meta description ne doit pas dépasser 160 caractères';
    }

    return newErrors;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  }, [errors]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      let categoryResponse;
      if (isEditMode) {
        categoryResponse = await dispatch(updateCategory(id, formData));
      } else {
        categoryResponse = await dispatch(createCategory(formData));
      }

      // Upload image if provided
      if (imageFile && categoryResponse.id) {
        setUploadingImage(true);
        await dispatch(uploadCategoryImage(categoryResponse.id, imageFile));
        setUploadingImage(false);
      }

      navigate('/admin/categories');
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, formData, id, isEditMode, navigate, validateForm, imageFile]);

  const handleCancel = useCallback(() => {
    const hasChanges = Object.keys(formData).some(key => {
      if (key === 'order') return formData[key] !== 0;
      return formData[key] !== '' && formData[key] !== false;
    }) || imageFile;

    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      navigate('/admin/categories');
    }
  }, [formData, imageFile, navigate]);

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  // Filter parent categories (exclude self and children in edit mode)
  const availableParents = categories.filter(cat => {
    if (!isEditMode) return true;
    if (cat.id === id) return false;
    // Add logic to exclude children if needed
    return true;
  });

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEditMode ? 'Modifier la catégorie' : 'Créer une nouvelle catégorie'}
        </h1>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearCategoryError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearCategorySuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basic Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Informations de base</h2>
          <div style={styles.grid}>
            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="name">
                  Nom *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.name ? styles.inputError : {}),
                  }}
                  placeholder="Nom de la catégorie"
                />
                {errors.name && (
                  <div style={styles.errorText}>{errors.name}</div>
                )}
              </div>
            </div>

            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="slug">
                  Slug *
                </label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.slug ? styles.inputError : {}),
                  }}
                  placeholder="nom-de-la-categorie"
                />
                {errors.slug && (
                  <div style={styles.errorText}>{errors.slug}</div>
                )}
                <div style={styles.slugPreview}>
                  URL: /categorie/{formData.slug || 'nom-de-la-categorie'}
                </div>
              </div>
            </div>

            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="Description de la catégorie"
                  rows="3"
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="type">
                Type *
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                style={{
                  ...styles.select,
                  ...(errors.type ? styles.inputError : {}),
                }}
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.type && (
                <div style={styles.errorText}>{errors.type}</div>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="parentId">
                Catégorie parente
              </label>
              <select
                id="parentId"
                name="parentId"
                value={formData.parentId}
                onChange={handleChange}
                style={styles.select}
              >
                <option value="">Aucune (catégorie racine)</option>
                {availableParents.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}{/* {cat.name} ({typeLabels[cat.type] || cat.type}) */}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Visual Identity */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Identité visuelle</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Icône</label>
              <input
                type="text"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                style={styles.input}
                placeholder="ex: 📁"
              />
              <div style={styles.helperText}>
                Emoji ou caractère spécial
              </div>
              <div style={styles.iconGrid}>
                {iconOptions.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    style={{
                      ...styles.iconButton,
                      ...(formData.icon === icon ? styles.iconButtonSelected : {}),
                    }}
                    onClick={() => setFormData(prev => ({ ...prev, icon }))}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Couleur</label>
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                style={{ ...styles.input, height: '40px', padding: '0.25rem' }}
              />
              <div style={styles.colorGrid}>
                {colorOptions.map(color => (
                  <button
                    key={color}
                    type="button"
                    style={{
                      ...styles.colorButton,
                      backgroundColor: color,
                      ...(formData.color === color ? styles.colorButtonSelected : {}),
                    }}
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                  />
                ))}
              </div>
            </div>

            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Image</label>
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={styles.imagePreview}
                  />
                )}
                <div style={styles.imageUpload}>
                  <input
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('image').click()}
                    style={styles.imageUploadButton}
                    disabled={uploadingImage}
                  >
                    <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {imagePreview ? "Changer l'image" : 'Ajouter une image'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SEO */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>SEO</h2>
          <div style={styles.grid}>
            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="metaTitle">
                  Meta titre
                </label>
                <input
                  type="text"
                  id="metaTitle"
                  name="metaTitle"
                  value={formData.metaTitle}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="Titre pour les moteurs de recherche"
                />
              </div>
            </div>

            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="metaDescription">
                  Meta description
                </label>
                <textarea
                  id="metaDescription"
                  name="metaDescription"
                  value={formData.metaDescription}
                  onChange={handleChange}
                  style={{
                    ...styles.textarea,
                    ...(errors.metaDescription ? styles.inputError : {}),
                  }}
                  placeholder="Description pour les moteurs de recherche"
                  rows="2"
                />
                {errors.metaDescription && (
                  <div style={styles.errorText}>{errors.metaDescription}</div>
                )}
                <div style={styles.helperText}>
                  {formData.metaDescription?.length || 0}/160 caractères
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Settings */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Paramètres</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                Catégorie active
              </label>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="order">
                Ordre d'affichage
              </label>
              <input
                type="number"
                id="order"
                name="order"
                value={formData.order}
                onChange={handleChange}
                style={styles.input}
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting || uploadingImage}
            style={{
              ...styles.submitButton,
              ...((isSubmitting || uploadingImage) ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting && !uploadingImage) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting && !uploadingImage) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {isSubmitting || uploadingImage
              ? (uploadingImage ? 'Téléchargement...' : (isEditMode ? 'Modification...' : 'Création...'))
              : (isEditMode ? 'Mettre à jour' : 'Créer la catégorie')}
          </button>
        </div>
      </form>

      {/* Cancel Confirmation */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate('/admin/categories')}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer l'édition"
        type="warning"
      />
    </div>
  );
};

export default CategoryForm;